import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  useGetCmsComponentsStripQuery,
  useGetCmsAccessoriesStripQuery,
  ahuBoardFormsApi,
} from 'app/services/ahuBoardFormsApi';
import { useGetAhuUnitQuery } from 'app/services/ahuBoardApi';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';

/**
 * Phase 6e1 — insert toolbar (§9.1, toolbar.components.js +
 * toolbar.components.tmpl, markup faithful; [NEG]#50 noise dropped): two icon
 * strips from the CMS endpoints, DOUBLE-CLICK to insert. Components: empty
 * AHU + non-dual → immediate `create:component:any` in tunnel index 1; else
 * the ADD Section popup (host raises it). Accessories: `create:accessory:any`
 * + optimistic skeleton row; strip `.non-clickable` without a selection;
 * drain_pan gated per-section-type via available_sections (uvc is not — see below).
 */

interface InsertStripProps {
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  onOpenInsertPopup: (args: {
    name: string;
    value: string;
    type: string;
  }) => void;
  /** Hybrid AHU (§4): a Power user on a still-empty AHU always gets the
   * Add-Section popup (to choose Standard/Hybrid) instead of the direct-create
   * fast path — only they can bootstrap a hybrid AHU. */
  isPowerUser: boolean;
}

const InsertStrip = ({ tunnels, sections, onOpenInsertPopup, isPowerUser }: InsertStripProps) => {
  const dispatch = useAppDispatch();
  const { ahuId: ahuIdParam } = useParams();
  const ahuId = Number(ahuIdParam);
  const { data: components } = useGetCmsComponentsStripQuery();
  const { data: cmsAccessories } = useGetCmsAccessoriesStripQuery();
  // VFD is excluded from the strip (toolbar.components.js:96,
  // `.filter(accessory => accessory.value !== 'vfd')`, dev 5e652d4051 "Exclude
  // VFD from Accessories"). The CMS catalog endpoint still returns the row —
  // legacy dropped it in the view, because a VFD is never inserted from here:
  // it is created by ticking Has VFD on a Fan (FanVfdTab → vfd accessory), and
  // its row is likewise undeletable from the Accessories tab when it carries a
  // fan_motor_id (TabAccessories AccessoryRemoveIcon). The commit is merged
  // into this branch but patched a file the migration deleted, so the filter
  // was lost with it.
  const accessories = useMemo(
    () => (cmsAccessories ?? []).filter((a) => String(a.value ?? '') !== 'vfd'),
    [cmsAccessories]
  );
  // AHU construction class — drain_pan's available_sections is now scoped to it
  // (legacy DrainPanView ahu.get('construction_class_id'), default "Standard"=1).
  const { data: ahuUnit } = useGetAhuUnitQuery(Number.isFinite(ahuId) ? ahuId : skipToken);
  const constructionClassId = ahuUnit?.construction_class_id ?? 1;
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const [availableByName, setAvailableByName] = useState<Record<string, string[]>>({});

  const currentSection = sections.find((s) => s.id === currentSectionId);

  // csa_available_sections (§9.1) — drain_pan ONLY.
  //
  // ★ UV-C is deliberately NOT gated, matching legacy. toolbar.components.js:45
  // declares `component_specific_accessories: ['drain_pan','uvc']`, but the
  // filter at :103 tests those keys against the accessory's `value`, and UV-C's
  // value is `uvc_system` (ksdm_master_ahu_accessory id=13) — never 'uvc'. So
  // `!~indexOf('uvc_system')` is always true and UV-C falls through to
  // available_accessories on every section type. The handler is still fetched
  // and stashed in stateStorage, but nothing ever matches it: the gate is dead
  // config, not behaviour. Porting the *intent* rather than the effect made
  // UV-C non-clickable on dev3 for any section outside
  // ksdm_cms_ahu_uvc_available_sections (Coil/Eliminator/Heat Wheel/
  // Split-Plenum on mfg 1) — e.g. greyed out on a Plenum, where dev enables it.
  // Do not "fix" this back without a product decision + CMS data to match.
  //
  // This does NOT apply to DuplicateAccessoryPopup, which keys the same lookup
  // off the accessory's `type` (duplicate_accessory.js:49-53) — and a UV-C
  // accessory's type IS 'uvc', so there the gate is live in legacy too. Its
  // CSA_TYPES = ['drain_pan','uvc'] is correct and fetches independently.
  useEffect(() => {
    const gated = ['drain_pan'];
    (accessories ?? [])
      .filter((a) => gated.includes(String(a.value ?? '')))
      .forEach((a) => {
        const name = String(a.value ?? '');
        dispatch(
          ahuBoardFormsApi.endpoints.getAccessoryAvailableSections.initiate({
            name,
            constructionClassId,
          })
        )
          .unwrap()
          .then((rows) => {
            setAvailableByName((prev) => ({
              ...prev,
              // the serializer exposes the section type as `section_type`
              // (CMSAHUDrainPanAvailableSectionsSerializer source='section.value');
              // legacy gate keys off av_section.get('section_type')
              // (toolbar.components.js:108).
              [name]: rows.map((r) =>
                String(r.section_type ?? r.type ?? r.name ?? '').toLowerCase()
              ),
            }));
          })
          .catch(() => undefined);
      });
  }, [accessories, dispatch, constructionClassId]);

  const emptyAhu =
    !tunnels.length || !sections.some((s) => s.tunnel_id === tunnels[0]?.id);

  const onComponentDblClick = (row: Record<string, unknown>) => {
    const value = String(row.value ?? '');
    const name = String(row.name ?? '');
    const type = String(row.type ?? '');
    // toolbar.components.js (post-6486aeca38/7a9b0e0a5a): a Standard user is
    // never offered the Standard/Hybrid toggle, so on an empty AHU there is
    // nothing to ask — skip straight to direct-create.
    //
    // A Power User always gets asked, including on an AHU that WAS hybrid
    // before every section was deleted off it: an empty AHU has no
    // configuration any more (the server clears `is_hybrid` on the delete
    // paths — api/rest_ahu/utils.py `reset_hybrid_mode_if_empty`), so the
    // choice is open again. This used to short-circuit on `ahu.is_hybrid`,
    // which is what pinned such an AHU to Hybrid for good.
    if (emptyAhu && !isPowerUser && type !== 'dual_tunnel') {
      const firstIndex = tunnels[0]?.index_id ?? 1;
      ahuBoardBus.emit('create:component:any', [
        {
          component_value: value,
          component_name: value === 'split_plenum' ? 'Plenum' : name,
          tunnel_index_id: firstIndex,
          component_type: type,
        },
      ]);
      return;
    }
    onOpenInsertPopup({
      name: value === 'split_plenum' ? 'Plenum' : name,
      value,
      type,
    });
  };

  const onAccessoryDblClick = (value: string, name: string) => {
    if (!currentSection) return;
    ahuBoardBus.emit('create:accessory:any', value);
    // Optimistic skeleton row — legacy create_accessory markup verbatim
    // (toolbar.components.js:187-200): label + wrapper preloader, keyed on the
    // CREATED accessory's type (uvc_system→uvc, combo_louver→louver — KA-5245)
    // so TabAccessories can remove it when the real row renders. The 5 s
    // timeout is a failure backstop only (QA batch 8 #3b).
    const loadingType =
      value === 'uvc_system' ? 'uvc' : value === 'combo_louver' ? 'louver' : value;
    const list = document.querySelector(
      `#forms_accessories_${currentSection.id} ul.inputs-group`
    );
    if (list && !list.querySelector(`.accessory-loading-${loadingType}`)) {
      const text = name.trim();
      const li = document.createElement('li');
      li.className = `accessory accessory-loading accessory-loading-${loadingType}`;
      li.innerHTML = `<label>${text !== 'UV-C' ? text : 'UVC'}</label>
        <div class="accessory-wrapper accessory-wrapper-preloader accessory-wrapper_${value}">
            <div class="preloader dotted-preloader active"></div>
        </div>`;
      list.appendChild(li);
      window.setTimeout(() => li.remove(), 5000);
    }
  };

  const accessoryAvailable = (value: string): boolean => {
    const gate = availableByName[value];
    if (!gate) return true;
    const type = String(currentSection?.type ?? '').toLowerCase();
    // legacy underscore↔space variant (toolbar.components.js:108):
    // `section_type == current_section_type || section_type.replace('_',' ') == …`
    return gate.some((st) => st === type || st.replace('_', ' ') === type);
  };

  return (
    <>
      <ul className="components-list conected-to-error-log">
        {(components ?? []).map((row) => {
          const value = String(row.value ?? '');
          return (
            <li key={value}>
              <input id={`${value}_button`} name={value} type="checkbox" readOnly />
              <label
                htmlFor={`${value}_button`}
                data-name={String(row.name ?? '')}
                data-value={value}
                data-type={String(row.type ?? '')}
                onDoubleClick={() => onComponentDblClick(row)}
              >
                <i
                  className={`icon-${value}`}
                  data-name={String(row.name ?? '')}
                  data-value={value}
                  data-type={String(row.type ?? '')}
                />{' '}
                {/* toolbar.components.tmpl:7-8 puts `{{name}}` on its own line
                    after the `<i>`, so legacy renders one collapsed word-space
                    between glyph and label. No CSS supplies that gap — dropping
                    it in JSX closed the icon/label spacing by ~3px AND, because
                    both lists are right-anchored, shifted every row 3px right
                    (measured: li 113px on dev, 110px on dev3). */}
                {String(row.name ?? '')}
              </label>
            </li>
          );
        })}
      </ul>

      <ul
        className={`accessories-list conected-to-error-log${
          currentSection ? '' : ' non-clickable'
        }`}
      >
        {(accessories ?? []).map((row) => {
          const value = String(row.value ?? '');
          return (
            <li
              key={value}
              data-name={value}
              className={accessoryAvailable(value) ? undefined : 'non-clickable'}
            >
              <input id={`${value}_button`} name={value} type="checkbox" readOnly />
              <label
                htmlFor={`${value}_button`}
                className={`${value}_label`}
                data-name={value}
                onDoubleClick={() => onAccessoryDblClick(value, String(row.name ?? ''))}
              >
                {value !== 'dog_house' ? (
                  // Combo Louver (KA-5245) — A0.1 icon exception.
                  // toolbar.components.tmpl:24-27 special-cases the class:
                  //   {{#ifCompare value '==' 'combo_louver'}}<i class="icon-louver" …>
                  // ★ NOT because the glyph is missing — `icon-combo_louver` IS
                  // defined (common.css:30447, U+E063, identical to icon-louver)
                  // and already renders. The defect is the COLOUR: the olive tint
                  // rule is keyed to `i.icon-louver` only
                  // (ahu/styles.css:9396-9398, source _ui.section_accss_group
                  // .scss:62-64 → #827717) and there is NO icon-combo_louver
                  // colour rule anywhere. Measured (inventory §7.2): legacy
                  // rgb(130,119,23) vs React rgb(117,117,117). Emitting
                  // `icon-louver` is parity-exact and inherits the tint — do NOT
                  // add CSS.
                  <i className={`icon-${value === 'combo_louver' ? 'louver' : value}`} data-name={value} />
                ) : (
                  <i className={`icon-${value} material-symbols-outlined`} data-name={value}>
                    {''}
                  </i>
                )}{' '}
                {/* Same word-space as the components list above
                    (toolbar.components.tmpl:32). */}
                {String(row.name ?? '')}
              </label>
            </li>
          );
        })}
      </ul>
      <span className="accessories-disable-note hide">Select a Section</span>
    </>
  );
};

export default InsertStrip;
